/**
 * Timezone utilities for listing timezone support
 */

export interface TimezoneOption {
  value: string;
  label: string;
}

/**
 * Common US and Canada IANA timezones with DST support
 */
export const COMMON_TIMEZONES: TimezoneOption[] = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Phoenix', label: 'Mountain Time - Arizona (no DST)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT)' },
  { value: 'America/Toronto', label: 'Eastern Time - Toronto' },
  { value: 'America/Winnipeg', label: 'Central Time - Winnipeg' },
  { value: 'America/Edmonton', label: 'Mountain Time - Edmonton' },
  { value: 'America/Vancouver', label: 'Pacific Time - Vancouver' },
  { value: 'America/Halifax', label: 'Atlantic Time - Halifax' },
  { value: 'America/St_Johns', label: 'Newfoundland Time' },
];

/**
 * Format a date in a specific timezone
 * @param date - Date to format
 * @param timezone - IANA timezone string
 * @param options - Intl.DateTimeFormatOptions
 * @returns Formatted date string
 */
export function formatDateInTimezone(
  date: Date,
  timezone: string,
  options?: Intl.DateTimeFormatOptions
): string {
  return format(date, timezone, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
    ...options,
  });
}

/**
 * Formats against a complete option set. Callers that want fewer fields have
 * to start here rather than from formatDateInTimezone, since spreading over
 * its defaults can only override a field, never drop one.
 */
function format(date: Date, timezone: string, options: Intl.DateTimeFormatOptions): string {
  return new Intl.DateTimeFormat('en-US', { ...options, timeZone: timezone }).format(date);
}

/**
 * Format a date with both listing timezone and UTC
 * @param date - Date to format
 * @param timezone - IANA timezone string
 * @returns Formatted string showing both timezones
 */
export function formatDateWithBothTimezones(date: Date, timezone: string): string {
  const localTime = formatDateInTimezone(date, timezone);
  const utcTime = formatDateInTimezone(date, 'UTC', { timeZoneName: 'short' });
  return `${localTime} (${utcTime})`;
}

/**
 * Get timezone abbreviation for a given date
 * @param date - Date to check
 * @param timezone - IANA timezone string
 * @returns Timezone abbreviation (e.g., 'EST', 'EDT', 'PST')
 */
export function getTimezoneAbbreviation(date: Date, timezone: string): string {
  const formatted = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    timeZoneName: 'short',
  }).format(date);

  // Extract the timezone abbreviation (last part after space)
  const parts = formatted.split(' ');
  return parts[parts.length - 1] || '';
}

/**
 * Format time only in a specific timezone
 * @param date - Date to format
 * @param timezone - IANA timezone string
 * @returns Formatted time string (e.g., "2:00 PM EST")
 */
export function formatTimeInTimezone(date: Date, timezone: string): string {
  return format(date, timezone, {
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  });
}

/**
 * Format date only in a specific timezone
 * @param date - Date to format
 * @param timezone - IANA timezone string
 * @returns Formatted date string (e.g., "Jan 15, 2025")
 */
export function formatDateOnlyInTimezone(date: Date, timezone: string): string {
  return format(date, timezone, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Format a date only, in a specific timezone, with the weekday abbreviated
 * (e.g. "Thu, Jun 12, 2025").
 *
 * The weekday earns its place wherever a date is read against a weekly
 * pattern - a repeating window's blackout dates, say, where the owner picked
 * the days of the week the window runs on.
 * @param date - Date to format
 * @param timezone - IANA timezone string
 * @returns Formatted date string (e.g. "Thu, Jun 12, 2025")
 */
export function formatDateOnlyWithWeekdayInTimezone(date: Date, timezone: string): string {
  return format(date, timezone, {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Converts a date-only DB value (stored as UTC midnight, e.g. specificDate,
 * repeatStartDate/EndDate, a repeatExceptions entry) into a local Date at
 * local midnight for the *same calendar day*. This makes it directly
 * comparable (via getTime()) to calendar-grid Date objects that pages build
 * with `new Date(); d.setHours(0,0,0,0)`, which are already local-midnight
 * for their intended day.
 */
export function dateOnlyFieldToLocalMidnight(value: Date | string): Date {
  const d = new Date(value);
  return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

/**
 * Format a date only, in a specific timezone, with the full weekday and
 * month names spelled out (e.g. "Saturday, June 14, 2025").
 *
 * This is the timezone-aware equivalent of `formatDateLong` in `lib/utils.ts`.
 * Renter-facing screens should use this (with the listing's timezone) rather
 * than `formatDateLong`, since a slot's UTC instant can fall on a different
 * calendar day in the browser's local timezone than it does in the listing's.
 * @param date - Date to format
 * @param timezone - IANA timezone string
 * @returns Formatted date string (e.g., "Saturday, June 14, 2025")
 */
export function formatDateLongInTimezone(date: Date, timezone: string): string {
  return format(date, timezone, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * The IANA timezone the browser is currently running in. Used only as a
 * last-resort fallback when a listing's timezone is unexpectedly missing —
 * every real listing has one (it is a required field with a default), so
 * this should not normally be reached.
 */
export function getBrowserTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * Build the UTC instant for a wall-clock date/time in a specific timezone.
 *
 * This is the inverse of the formatters above: given a listing's timezone
 * and the wall-clock time a window is meant to start/end at (e.g. "8:00 PM"
 * on a given calendar day), it produces the same UTC instant the backend
 * generates for that slot (see `createDateInTimezoneSimple` in
 * `backend/src/utils/timezone.ts`, which this mirrors). Anything that needs
 * to reconstruct a slot's instant on the frontend — rather than just format
 * one it already received from the API — should go through this rather than
 * `Date.setHours`, which sets wall-clock components in the browser's
 * timezone, not the listing's.
 *
 * @param year - Full year (e.g. 2025)
 * @param month - Month, 1-12
 * @param day - Day of month
 * @param hours - Hours, 0-23
 * @param minutes - Minutes, 0-59
 * @param timezone - IANA timezone string
 * @returns Date object representing that wall-clock time in the given timezone
 */
export function createDateInTimezone(
  year: number,
  month: number,
  day: number,
  hours: number,
  minutes: number,
  timezone: string
): Date {
  // Read the wall-clock values as if they were UTC, then correct by however far
  // the target timezone sits from UTC at that moment.
  const asIfUtc = Date.UTC(year, month - 1, day, hours, minutes, 0, 0);

  const firstGuess = asIfUtc - offsetAt(asIfUtc, timezone);

  // The offset has to be measured at the instant we are actually returning, not
  // at the naive one, or a time near a DST change lands an hour out.
  const corrected = asIfUtc - offsetAt(firstGuess, timezone);

  return new Date(corrected);
}

/**
 * How far ahead of UTC the timezone is at a given instant, in milliseconds.
 *
 * Compares the wall clock in the zone against the wall clock in UTC. Comparing
 * whole dates rather than just the hour matters: any conversion where the two
 * zones are on different calendar days would otherwise come out a day off.
 */
function offsetAt(utcMs: number, timezone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date(utcMs));

  const value = (type: Intl.DateTimeFormatPartTypes): number =>
    Number(parts.find(part => part.type === type)?.value ?? 0);

  const wallClock = Date.UTC(
    value('year'),
    value('month') - 1,
    value('day'),
    value('hour'),
    value('minute'),
    value('second')
  );

  // Sub-second precision is not represented in the parts, so drop it from both
  // sides before subtracting.
  return wallClock - Math.floor(utcMs / 1000) * 1000;
}

/**
 * Get the current UTC offset for a timezone
 * @param timezone - IANA timezone string
 * @param date - Date to check (defaults to now)
 * @returns UTC offset in hours (e.g., -5 for EST, -4 for EDT)
 */
export function getTimezoneOffset(timezone: string, date: Date = new Date()): number {
  // Create a formatter that includes timezone offset
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    timeZoneName: 'longOffset',
  });

  const parts = formatter.formatToParts(date);
  const offsetPart = parts.find(part => part.type === 'timeZoneName');
  
  if (offsetPart && offsetPart.value.startsWith('GMT')) {
    // Parse "GMT-5" or "GMT+5:30" format
    const match = offsetPart.value.match(/GMT([+-])(\d+)(?::(\d+))?/);
    if (match) {
      const sign = match[1] === '+' ? 1 : -1;
      const hours = parseInt(match[2], 10);
      const minutes = match[3] ? parseInt(match[3], 10) : 0;
      return sign * (hours + minutes / 60);
    }
  }

  return 0;
}

